import { Button, Group, Table, Text, Tooltip } from '@mantine/core';
import { countWord } from '../../domain/pluralise.js';
import { isActionable, isAdoptable } from '../../domain/status.js';
import { SyncStatusBadge } from '../StatusBadge/StatusBadge.js';
import type { EntityDetailState } from './useEntityDetail.js';

type EnvironmentRow = NonNullable<EntityDetailState['detail']>['environments'][number];

/**
 * This entity across every environment: status and which overrides applied. **Compare** opens
 * the field diff for that environment, whatever its status (for `missing`, the record a push
 * would create). **Adopt** is offered where the environment differs from master: an `env_only`
 * record is copied into master, a `drift` one keeps that environment's values as its own
 * (`hooks/useAdopt.tsx`).
 *
 * The override count is what answers "why does this one differ" without opening the JSON, so
 * it sits next to the environment name rather than in the master panel.
 */
export const EnvironmentsPanel = ({
    environments,
    onFocus,
    onPush,
    onAdopt,
}: {
    environments: EnvironmentRow[];
    onFocus: (environment: string) => void;
    /** Absent where the hosting page cannot push: the Push action is then not offered. */
    onPush?: (environment: string) => void;
    onAdopt: (environment: string) => void;
}) => (
    <Table verticalSpacing={6}>
        <Table.Thead>
            <Table.Tr>
                <Table.Th>Environment</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
            </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
            {environments.map((environment) => (
                <Table.Tr key={environment.name}>
                    <Table.Td>
                        <Text className="rgs-numeric" fz="xs">
                            {environment.name}
                        </Text>
                        {environment.appliedOverrides.length > 0 && (
                            <Tooltip
                                withArrow
                                label={`Overrides ${environment.appliedOverrides.join(', ')} apply here`}
                            >
                                <Text fz={10} c="dimmed">
                                    {countWord(environment.appliedOverrides.length, 'override')}
                                </Text>
                            </Tooltip>
                        )}
                    </Table.Td>
                    <Table.Td>
                        <SyncStatusBadge
                            status={environment.status}
                            size="xs"
                            tooltip={
                                environment.status === 'missing'
                                    ? `Not in ${environment.name} yet. Push creates it.`
                                    : undefined
                            }
                        />
                    </Table.Td>
                    <Table.Td>
                        <Group gap={4} wrap="nowrap">
                            <Button
                                size="compact-xs"
                                variant="subtle"
                                aria-label={`Compare with ${environment.name}`}
                                onClick={() => onFocus(environment.name)}
                            >
                                Compare
                            </Button>
                            {onPush && isActionable(environment.status) && (
                                <Button size="compact-xs" variant="light" onClick={() => onPush(environment.name)}>
                                    Push
                                </Button>
                            )}
                            {isAdoptable(environment.status) && (
                                <Button
                                    size="compact-xs"
                                    variant="light"
                                    color="blue"
                                    aria-label={`Adopt from ${environment.name}`}
                                    onClick={() => onAdopt(environment.name)}
                                >
                                    Adopt
                                </Button>
                            )}
                        </Group>
                    </Table.Td>
                </Table.Tr>
            ))}
        </Table.Tbody>
    </Table>
);
